import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  CheckCircleOutline,
  ErrorOutline,
  RefreshOutlined,
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import SearchFeaturePageShell from '../../components/shell/SearchFeaturePageShell';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const useStyles = makeStyles(theme => ({
  summary: {
    marginBottom: theme.spacing(3),
  },
  card: {
    height: '100%',
    padding: theme.spacing(2.5),
    border: '1px solid #e0ebe7',
    borderRadius: 10,
    boxShadow: 'none',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  ok: { color: theme.palette.success.main },
  failed: { color: theme.palette.error.main },
  note: { marginTop: theme.spacing(3) },
}));

function configDescription(config, t) {
  const experience = config?.search?.searchExperience || 'not configured';
  const viewCount = Object.keys(config?.search?.views || {}).length;
  return t('ui.home.cloudDiagnosticsScreen.configDescription', { experience, viewCount });
}

function resultFrom(settled, describe, t) {
  if (settled.status === 'fulfilled') {
    return { ok: true, detail: describe(settled.value) };
  }
  return {
    ok: false,
    detail: settled.reason?.message || t('ui.home.cloudDiagnosticsScreen.requestFailed'),
  };
}

export default function CloudDiagnosticsScreen() {
  const classes = useStyles();
  const t = useLocalizedMessage();
  const shopId = useSelector(state => state.app?.currentShop);
  const { searchSettingsClient } = useShellAppContext();
  const [checks, setChecks] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fatalError, setFatalError] = useState(null);

  const runChecks = useCallback(async () => {
    if (!shopId || !searchSettingsClient) {
      setFatalError(t('ui.home.cloudDiagnosticsScreen.settingsUnavailable'));
      setChecks(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setFatalError(null);
    const settled = await Promise.allSettled([
      searchSettingsClient.loadSearchExperienceConfig(shopId, 'preview'),
      searchSettingsClient.loadSearchExperienceConfig(shopId, 'production'),
      searchSettingsClient.describeSearchExperienceConfig(shopId),
      searchSettingsClient.getSearchComponents(shopId),
    ]);

    setChecks([
      {
        id: 'preview',
        label: "ui.home.cloudDiagnosticsScreen.previewSettings",
        ...resultFrom(settled[0], value => configDescription(value, t), t),
      },
      {
        id: 'production',
        label: "ui.home.cloudDiagnosticsScreen.productionSettings",
        ...resultFrom(settled[1], value => configDescription(value, t), t),
      },
      {
        id: 'schema',
        label: "ui.home.cloudDiagnosticsScreen.v2SchemaContract",
        ...resultFrom(
          settled[2],
          value => t('ui.home.cloudDiagnosticsScreen.schemaAvailable', {
            version: value?.schemaVersion || t('ui.home.cloudDiagnosticsScreen.available'),
          }),
          t,
        ),
      },
      {
        id: 'components',
        label: "ui.home.cloudDiagnosticsScreen.renderingComponents",
        ...resultFrom(
          settled[3],
          value => t('ui.home.cloudDiagnosticsScreen.descriptorsAvailable', {
            count: Object.keys(value?.components || value || {}).length,
          }),
          t,
        ),
      },
    ]);
    setIsLoading(false);
  }, [searchSettingsClient, shopId, t]);

  useEffect(() => {
    runChecks();
  }, [runChecks]);

  const failedCount = useMemo(
    () => (checks || []).filter(check => !check.ok).length,
    [checks],
  );

  return (
    <SearchFeaturePageShell
      pageId="diagnostics"
      description={<IntlMessages id="ui.home.cloudDiagnosticsScreen.verifyTheCloudSettingsContractAndTheStateUsedTo" />}
      actions={(
        <Button
          variant="outlined"
          color="primary"
          startIcon={<RefreshOutlined />}
          onClick={runChecks}
          disabled={isLoading}
        >
          <IntlMessages id="ui.home.cloudDiagnosticsScreen.runChecksAgain" />
        </Button>
      )}
      isLoading={isLoading}
    >
      <Box data-testid="cloud-diagnostics">
        {fatalError && <Alert severity="error" className={classes.summary}>{fatalError}</Alert>}
        {!fatalError && checks && (
          <Alert severity={failedCount === 0 ? 'success' : 'warning'} className={classes.summary}>
            {failedCount === 0
              ? t('ui.home.cloudDiagnosticsScreen.contractsReachable')
              : t('ui.home.cloudDiagnosticsScreen.checksNeedAttention', { count: failedCount })}
          </Alert>
        )}

        <Grid container spacing={2}>
          {(checks || []).map(check => (
            <Grid item xs={12} sm={6} key={check.id}>
              <Paper className={classes.card} elevation={0} data-testid={`cloud-diagnostic-${check.id}`}>
                <Box className={classes.title}>
                  {check.ok
                    ? <CheckCircleOutline className={classes.ok} aria-label={t('ui.home.cloudDiagnosticsScreen.passed')} />
                    : <ErrorOutline className={classes.failed} aria-label={t('ui.home.cloudDiagnosticsScreen.failed')} />}
                  <Typography variant="subtitle1">{t(check.label)}</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">{check.detail}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Alert severity="info" className={classes.note}>
          <IntlMessages id="ui.home.cloudDiagnosticsScreen.catalogReindexingRunsAsABackgroundWebserviceTaskTheCurrent" />
        </Alert>
      </Box>
    </SearchFeaturePageShell>
  );
}
